/**
 * Lost Sales calculation settings (`dashboard.lost_sales` in clientConfig).
 *
 * The server owns the defaults, limits and validation and returns them with
 * the availability check (`GET /lost-sales` → `settings`); this file only
 * describes them and maps the editor draft to the stored value.
 */
export const LOST_SALES_SETTINGS_KEY = "dashboard.lost_sales";

export const LOST_SALES_PARAMETER_HELP = {
  search_window_days:
    "How many days of history before the analysis period are used to estimate normal demand (in-stock days without recorded events).",
  dow_adjustment_enabled:
    "Estimate each day's demand from the same weekday (e.g. Mondays from past Mondays).",
  demand_window_days:
    "Estimate each day's demand from the nearest run of this many consecutive in-stock days, instead of all history.",
};

/** [{ key, label }] — e.g. "180-day search window", "Safe period off". */
export const describeLostSalesParameters = (parameters) => {
  if (!parameters) return [];
  return [
    {
      key: "search_window_days",
      label: `${parameters.search_window_days}-day search window`,
    },
    {
      key: "dow_adjustment_enabled",
      label: `Day-of-week adjustment ${parameters.dow_adjustment_enabled ? "on" : "off"}`,
    },
    {
      key: "demand_window_days",
      label: parameters.demand_window_days
        ? `${parameters.demand_window_days}-day safe period`
        : "Safe period off",
    },
  ];
};

export const draftFromParameters = (parameters, limits) => ({
  searchWindowDays: String(parameters.search_window_days),
  dowAdjustmentEnabled: parameters.dow_adjustment_enabled,
  safePeriodEnabled: parameters.demand_window_days != null,
  safePeriodDays: String(
    parameters.demand_window_days ?? limits?.demand_window_days?.suggested ?? 14,
  ),
});

const wholeNumber = (text) =>
  /^\d+$/.test(String(text).trim()) ? Number(String(text).trim()) : NaN;

/**
 * Field errors for the editor, mirroring the server rules so problems show
 * before saving. The server remains the authority.
 */
export const draftErrors = (draft, limits) => {
  const errors = {};
  const search = wholeNumber(draft.searchWindowDays);
  const searchLimits = limits.search_window_days;
  if (!(search >= searchLimits.min && search <= searchLimits.max)) {
    errors.searchWindowDays = `Enter ${searchLimits.min}–${searchLimits.max} days.`;
  }
  if (draft.safePeriodEnabled) {
    const safe = wholeNumber(draft.safePeriodDays);
    const safeLimits = limits.demand_window_days;
    if (!(safe >= safeLimits.min && safe <= safeLimits.max)) {
      errors.safePeriodDays = `Enter ${safeLimits.min}–${safeLimits.max} days.`;
    } else if (!errors.searchWindowDays && safe > search) {
      errors.safePeriodDays = "Can't be longer than the search window.";
    }
  }
  return errors;
};

export const draftToValue = (draft) => ({
  schema_version: 1,
  parameters: {
    search_window_days: wholeNumber(draft.searchWindowDays),
    dow_adjustment_enabled: draft.dowAdjustmentEnabled,
    demand_window_days: draft.safePeriodEnabled
      ? wholeNumber(draft.safePeriodDays)
      : null,
  },
});

export const sameParameters = (left, right) =>
  Boolean(left && right) &&
  left.search_window_days === right.search_window_days &&
  left.dow_adjustment_enabled === right.dow_adjustment_enabled &&
  (left.demand_window_days ?? null) === (right.demand_window_days ?? null);
